import React from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import { ChartNoAxesColumn, CircleAlert, RotateCw } from 'lucide-react';
import { formatNumber, toCount } from './chartTheme';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

const Card = styled.section`
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: var(--border-radius-lg);
  padding: 1.25rem 1.5rem 1.5rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const HeaderText = styled.div`
  min-width: 0;
  flex: 1 1 260px;
`;

const Title = styled.h2`
  margin: 0;
  color: var(--color-text-primary);
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.35;
  text-wrap: balance;
`;

const Description = styled.p`
  margin: 0.25rem 0 0;
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  line-height: 1.45;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  line-height: 1.1;
  animation: ${fadeIn} 0.24s ease-out;

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const StatValue = styled.span`
  color: var(--color-text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.025em;
  font-variant-numeric: tabular-nums;
`;

const StatLabel = styled.span`
  margin-top: 0.25rem;
  color: var(--color-text-muted);
  font-size: 0.75rem;
`;

const Toolbar = styled.div`
  margin-top: 1rem;
`;

const Body = styled.div`
  margin-top: 1.25rem;
  min-width: 0;
  animation: ${fadeIn} 0.24s ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const MessageBox = styled.div`
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 1.5rem;
  text-align: center;
  border-radius: var(--border-radius-md);
  background: var(--color-bg-secondary);
`;

const MessageIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-bottom: 0.5rem;
  border-radius: 10px;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  color: ${({ $tone }) =>
    $tone === 'error' ? 'var(--badge-priority-high-fg)' : 'var(--color-text-muted)'};
`;

const MessageTitle = styled.p`
  margin: 0;
  color: var(--color-text-primary);
  font-size: 0.875rem;
  font-weight: 600;
`;

const MessageText = styled.p`
  margin: 0;
  max-width: 42ch;
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  line-height: 1.5;
`;

const RetryButton = styled.button`
  margin-top: 0.85rem;
`;

const ChartMessage = ({ icon: Icon, tone, title, description, action }) => (
  <MessageBox role={tone === 'error' ? 'alert' : undefined}>
    <MessageIcon $tone={tone} aria-hidden>
      <Icon size={18} strokeWidth={1.75} />
    </MessageIcon>
    <MessageTitle>{title}</MessageTitle>
    {description ? <MessageText>{description}</MessageText> : null}
    {action}
  </MessageBox>
);

ChartMessage.propTypes = {
  icon: PropTypes.elementType.isRequired,
  tone: PropTypes.oneOf(['neutral', 'error']),
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  action: PropTypes.node,
};

/* ---------- Skeletons ---------- */

const skeletonBlock = css`
  background: var(--color-bg-tertiary);
  border-radius: 6px;
  animation: ${pulse} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SkeletonRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  padding: 0.25rem 0;
`;

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: minmax(80px, 140px) 1fr;
  align-items: center;
  gap: 1rem;
`;

const SkeletonBar = styled.span`
  ${skeletonBlock};
  display: block;
  height: ${({ $h = 12 }) => `${$h}px`};
  width: ${({ $w = 100 }) => `${$w}%`};
  animation-delay: ${({ $delay = 0 }) => `${$delay}ms`};
`;

const SkeletonPlot = styled.div`
  position: relative;
  height: ${({ $h }) => `${$h}px`};
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  gap: 4%;
  padding: 0 2%;
  border-bottom: 1px solid var(--color-border-primary);
`;

const SkeletonColumn = styled.span`
  ${skeletonBlock};
  flex: 1;
  max-width: 28px;
  height: ${({ $h }) => `${$h}%`};
  border-radius: 6px 6px 0 0;
  animation-delay: ${({ $delay = 0 }) => `${$delay}ms`};
`;

const SkeletonDonutWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 2.5rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const SkeletonDonut = styled.span`
  ${skeletonBlock};
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: transparent;
  border: 28px solid var(--color-bg-tertiary);
  box-sizing: border-box;
`;

const SkeletonTiles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
`;

const SkeletonTile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--color-border-primary);
  border-radius: var(--border-radius-md);
`;

const BAR_WIDTHS = [92, 76, 64, 51, 38, 24];
const COLUMN_HEIGHTS = [38, 52, 46, 70, 58, 82, 64, 76, 55, 68];

const ChartSkeleton = ({ variant = 'bars', height = 300 }) => {
  if (variant === 'donut') {
    return (
      <SkeletonDonutWrap aria-hidden>
        <SkeletonDonut />
        <SkeletonRows style={{ minWidth: 220 }}>
          {[80, 64, 52, 40].map((w, i) => (
            <SkeletonBar key={w} $w={w} $delay={i * 90} />
          ))}
        </SkeletonRows>
      </SkeletonDonutWrap>
    );
  }
  if (variant === 'tiles') {
    return (
      <SkeletonTiles aria-hidden>
        {[0, 1, 2].map((tile) => (
          <SkeletonTile key={tile}>
            <SkeletonBar $w={45} $h={14} $delay={tile * 90} />
            {[70, 48, 30].map((w, i) => (
              <SkeletonBar key={w} $w={w} $h={16} $delay={tile * 90 + i * 60} />
            ))}
          </SkeletonTile>
        ))}
      </SkeletonTiles>
    );
  }
  if (variant === 'columns') {
    return (
      <SkeletonPlot $h={height} aria-hidden>
        {COLUMN_HEIGHTS.map((h, i) => (
          <SkeletonColumn key={i} $h={h} $delay={i * 60} />
        ))}
      </SkeletonPlot>
    );
  }
  return (
    <SkeletonRows aria-hidden>
      {BAR_WIDTHS.map((w, i) => (
        <SkeletonRow key={w}>
          <SkeletonBar $w={70} $delay={i * 70} />
          <SkeletonBar $w={w} $h={18} $delay={i * 70} />
        </SkeletonRow>
      ))}
    </SkeletonRows>
  );
};

ChartSkeleton.propTypes = {
  variant: PropTypes.oneOf(['bars', 'columns', 'donut', 'tiles']),
  height: PropTypes.number,
};

/* ---------- Card ---------- */

const ChartCard = ({
  title,
  description = null,
  stat = null,
  statLabel = null,
  toolbar = null,
  loading = false,
  error = false,
  onRetry = null,
  isEmpty = false,
  emptyTitle = 'Sem dados para exibir',
  emptyDescription = 'Ajuste os filtros ou selecione outro período para ver resultados.',
  skeleton = 'bars',
  skeletonHeight = 300,
  children,
}) => {
  const descriptionText = Array.isArray(description)
    ? description.filter(Boolean).join(' · ')
    : description;

  let state = 'ready';
  if (loading) state = 'loading';
  else if (error) state = 'error';
  else if (isEmpty) state = 'empty';

  let body = children;
  if (state === 'loading') {
    body = <ChartSkeleton variant={skeleton} height={skeletonHeight} />;
  } else if (state === 'error') {
    body = (
      <ChartMessage
        icon={CircleAlert}
        tone="error"
        title="Não foi possível carregar os dados"
        description="Verifique sua conexão e tente novamente. Se o problema continuar, contate o suporte."
        action={
          onRetry ? (
            <RetryButton type="button" className="button button-secondary" onClick={onRetry}>
              <RotateCw size={14} strokeWidth={2} aria-hidden />
              Tentar novamente
            </RetryButton>
          ) : null
        }
      />
    );
  } else if (state === 'empty') {
    body = (
      <ChartMessage icon={ChartNoAxesColumn} title={emptyTitle} description={emptyDescription} />
    );
  }

  return (
    <Card aria-busy={loading || undefined}>
      <Header>
        <HeaderText>
          <Title>{title}</Title>
          {descriptionText ? <Description>{descriptionText}</Description> : null}
        </HeaderText>
        {state === 'ready' && stat != null ? (
          <Stat>
            <StatValue>{formatNumber(stat)}</StatValue>
            {statLabel ? <StatLabel>{statLabel}</StatLabel> : null}
          </Stat>
        ) : null}
      </Header>
      {toolbar ? <Toolbar>{toolbar}</Toolbar> : null}
      <Body key={state}>{body}</Body>
    </Card>
  );
};

ChartCard.propTypes = {
  title: PropTypes.node.isRequired,
  description: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  stat: PropTypes.number,
  statLabel: PropTypes.node,
  toolbar: PropTypes.node,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onRetry: PropTypes.func,
  isEmpty: PropTypes.bool,
  emptyTitle: PropTypes.node,
  emptyDescription: PropTypes.node,
  skeleton: PropTypes.oneOf(['bars', 'columns', 'donut', 'tiles']),
  skeletonHeight: PropTypes.number,
  children: PropTypes.node,
};

/* ---------- Legend ---------- */

const LegendList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.35rem;
  list-style: none;
  margin: 0 0 1rem;
  padding: 0;
`;

const LegendItem = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  max-width: 100%;
  min-height: 28px;
  padding: 0 0.6rem;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  opacity: ${({ $dimmed }) => ($dimmed ? 0.4 : 1)};
  transition: opacity 0.15s ease, background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${({ $interactive }) => ($interactive ? 'var(--color-hover-overlay)' : 'transparent')};
    color: var(--color-text-primary);
  }
`;

const LegendSwatch = styled.span`
  flex-shrink: 0;
  background: ${({ $color }) => $color};
  ${({ $variant }) =>
    $variant === 'line'
      ? css`
          width: 12px;
          height: 3px;
          border-radius: 2px;
        `
      : css`
          width: 8px;
          height: 8px;
          border-radius: 2px;
        `}
`;

const LegendText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: min(220px, 60vw);
`;

export const ChartLegend = ({ items, variant = 'square', activeKey = null, onActiveChange = null, label }) => {
  const interactive = typeof onActiveChange === 'function';
  return (
    <LegendList aria-label={label}>
      {items.map((item) => (
        <li key={item.key}>
          <LegendItem
            as={interactive ? 'button' : 'span'}
            type={interactive ? 'button' : undefined}
            title={item.label}
            $interactive={interactive}
            $dimmed={activeKey != null && activeKey !== item.key}
            onMouseEnter={interactive ? () => onActiveChange(item.key) : undefined}
            onMouseLeave={interactive ? () => onActiveChange(null) : undefined}
            onFocus={interactive ? () => onActiveChange(item.key) : undefined}
            onBlur={interactive ? () => onActiveChange(null) : undefined}
          >
            <LegendSwatch $color={item.color} $variant={variant} aria-hidden />
            <LegendText>{item.label}</LegendText>
          </LegendItem>
        </li>
      ))}
    </LegendList>
  );
};

ChartLegend.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      color: PropTypes.string.isRequired,
    })
  ).isRequired,
  variant: PropTypes.oneOf(['square', 'line']),
  activeKey: PropTypes.string,
  onActiveChange: PropTypes.func,
  label: PropTypes.string,
};

/* ---------- Tooltip ---------- */

const TooltipBox = styled.div`
  min-width: 168px;
  max-width: 280px;
  padding: 0.6rem 0.75rem;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-secondary);
  border-radius: 10px;
  box-shadow: var(--shadow-lg);
  font-size: 0.8125rem;
  line-height: 1.4;
`;

const TooltipTitle = styled.div`
  margin-bottom: 0.45rem;
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 500;
`;

const TooltipRow = styled.div`
  display: grid;
  grid-template-columns: 8px 1fr auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.12rem 0;
  color: var(--color-text-secondary);

  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .value {
    color: var(--color-text-primary);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

const TooltipSwatch = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;

const TooltipTotal = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.4rem;
  padding-top: 0.4rem;
  border-top: 1px solid var(--color-border-primary);
  color: var(--color-text-muted);

  span {
    color: var(--color-text-primary);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

export const ChartTooltip = ({
  active,
  payload,
  label,
  labelFormatter = null,
  nameFormatter = null,
  showTotal = false,
  hideZero = false,
}) => {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;

  const items = payload
    .filter((item) => item && item.value != null && (!hideZero || toCount(item.value) !== 0))
    .sort((a, b) => toCount(b.value) - toCount(a.value));
  const total = items.reduce((sum, item) => sum + toCount(item.value), 0);
  const title = labelFormatter ? labelFormatter(label, payload) : label;

  return (
    <TooltipBox>
      {title ? <TooltipTitle>{title}</TooltipTitle> : null}
      {items.map((item) => (
        <TooltipRow key={item.dataKey ?? item.name}>
          <TooltipSwatch $color={item.color ?? item.payload?.fill} />
          <span className="name">{nameFormatter ? nameFormatter(item.name, item) : item.name}</span>
          <span className="value">{formatNumber(item.value)}</span>
        </TooltipRow>
      ))}
      {showTotal && items.length > 1 ? (
        <TooltipTotal>
          Total <span>{formatNumber(total)}</span>
        </TooltipTotal>
      ) : null}
    </TooltipBox>
  );
};

ChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  labelFormatter: PropTypes.func,
  nameFormatter: PropTypes.func,
  showTotal: PropTypes.bool,
  hideZero: PropTypes.bool,
};

/* ---------- Axis tick ---------- */

export const TruncatedTick = ({ x, y, payload, maxChars = 22 }) => {
  const raw = String(payload?.value ?? '').trim() || '—';
  const text = raw.length > maxChars ? `${raw.slice(0, maxChars - 1)}…` : raw;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={-8} textAnchor="end" fill="var(--color-text-secondary)" fontSize={12} dy={4}>
        <title>{raw}</title>
        {text}
      </text>
    </g>
  );
};

TruncatedTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.shape({ value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  maxChars: PropTypes.number,
};

export default ChartCard;
